import LOCALS from 'commons/locals';
import { FormLabel } from '../form-label';
import {
  Control,
  Controller,
  FieldErrors,
  UseFormSetValue,
  useWatch,
} from 'react-hook-form';
import i18n from 'i18n';
import {
  CreateProductFormSchemaType,
  generateNote,
  generateSubTitle,
  useGenerateDetailTitle,
} from '../common';
import { Cascader } from 'antd';
import { selectGlobalInfo } from 'store/slices/globalInfoSlice';
import { useAppSelector } from 'store/hooks';
import getCascaderFilter from 'utils/getCascaderFilter';
import { useCallback } from 'react';
import { DefaultOptionType } from 'types/base';
import findCascaderOptionById from 'utils/findCascaderOptionById';
import { productBoxByCategory } from 'apis/product-box';

const ProductCategory = ({
  control,
  errors,
  setValue,
}: {
  control: Control<CreateProductFormSchemaType>;
  errors: FieldErrors<CreateProductFormSchemaType>;
  setValue: UseFormSetValue<CreateProductFormSchemaType>;
}) => {
  const {
    productCategoryCascaderOptions,
    colorSelectOptions,
    hardwareSelectOptions,
  } = useAppSelector(selectGlobalInfo);

  const CascaderFilter = useCallback(
    (inputValue: string, path: DefaultOptionType[]) =>
      getCascaderFilter(inputValue, path),
    [],
  );

  const color = useWatch({
    control,
    name: 'color',
  });

  const hardware = useWatch({
    control,
    name: 'hardware',
  });

  const { generateDetailTitle } = useGenerateDetailTitle(control);

  return (
    <div className="flex items-center gap-2">
      <FormLabel required>{i18n.t(LOCALS.product_category)}:</FormLabel>
      <Controller
        name="productCategoryIdList"
        control={control}
        render={({ field }) => (
          <div className="relative w-full">
            <Cascader
              {...field}
              status={errors.productCategoryIdList ? 'error' : undefined}
              className="w-full"
              options={productCategoryCascaderOptions}
              placeholder={i18n.t(LOCALS.please_select)}
              onChange={(value) => {
                field.onChange(value);

                const isFirstLevelChanged = field.value[0] !== value[0];

                // 如果商品分类第一级发生变化，则清空材质和收藏合集，并重新生成 note
                if (isFirstLevelChanged) {
                  setValue('material', []);
                  setValue('collections', []);
                  const note = generateNote(
                    [],
                    hardware,
                    [], // 这里传空数组就行，因为 material 是 []
                    hardwareSelectOptions,
                  );
                  setValue('note', note);
                  setValue('facebookEnabled', 0);
                }

                // 自动修改商品名称和商品尺寸
                const target = findCascaderOptionById(
                  value[value.length - 1],
                  productCategoryCascaderOptions,
                );
                if (target) {
                  const newName = {
                    ja: target.nameJa || '',
                    en: target.nameEn || '',
                    zh_CN: target.nameZh || '',
                    zh_TW: target.nameTw || '',
                  };
                  setValue('name', newName);
                  setValue('size', {
                    ja: target.sizeJa || '',
                    en: target.sizeEn || '',
                    zh_CN: target.sizeZh || '',
                    zh_TW: target.sizeTw || '',
                  });

                  const subTitle = generateSubTitle(
                    newName,
                    color,
                    colorSelectOptions,
                  );
                  setValue('subTitle', subTitle);

                  const detailTitle = generateDetailTitle({
                    name: newName,
                    // 如果商品分类第一级发生变化，清空收藏合集
                    collections: isFirstLevelChanged ? [] : undefined,
                    material: isFirstLevelChanged ? [] : undefined,
                  });
                  setValue('detailTitle', detailTitle);
                }

                const leafCategoryId = value?.[value.length - 1];
                if (leafCategoryId) {
                  productBoxByCategory(Number(leafCategoryId)).then((box) => {
                    setValue('boxId', box?.id ?? null, { shouldDirty: true });
                  });
                }
              }}
              showSearch={{ filter: CascaderFilter }}
              allowClear={false}
            />
            {errors.productCategoryIdList && (
              <p style={{ color: 'red' }} className="absolute left-0">
                {i18n.t(LOCALS.required_field)}
              </p>
            )}
          </div>
        )}
      />
    </div>
  );
};

export default ProductCategory;
